import { shortLinksDeployed, shortLinksUrl } from "@playbrain/config"
import type { AuthSession, SessionResponse } from "./types"

/**
 * Ask for a sign-in code.
 *
 * Always resolves. The server answers 200 whether or not it sent anything,
 * because the dashboard has no signup and a reply that distinguished "no such
 * account" from "code sent" would turn the login screen into a staff
 * directory. The UI must therefore say "check your inbox" either way.
 *
 */
export async function requestSignInCodeAction(email: string): Promise<void> {
  const response = await fetch("/api/dashboard/auth/otp", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ email }),
  })
  if (!response.ok) {
    const payload = (await response.json()) as { error?: string }
    throw new Error(payload.error ?? "Could not send a code.")
  }
}

/** Redeem a code. The address comes from the server's cookie, never from here. */
export async function submitSignInCodeAction(email: string, code: string): Promise<AuthSession> {
  const response = await fetch("/api/dashboard/auth/otp", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ email, code }),
  })
  const payload = (await response.json()) as { error?: string }
  if (!response.ok) throw new Error(payload.error ?? "That code is not valid.")
  return fetchServerSession()
}

/**
 * Begin Google sign-in.
 *
 * Returns a URL rather than redirecting so the caller performs a TOP-LEVEL
 * navigation. The PKCE verifier cookie is set on this response, and the return
 * leg from Supabase is a cross-site redirect -- an iframe or a background fetch
 * would lose it.
 */
export async function startGoogleSignInAction(returnTo?: string) {
  const response = await fetch("/api/dashboard/auth/sso", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ returnTo }),
  })
  const payload = (await response.json()) as { url?: string; error?: string }
  if (!response.ok || !payload.url) {
    throw new Error(payload.error ?? "Could not start Google sign-in.")
  }
  window.location.assign(payload.url)
}

export async function performSessionRefreshAction() {
  const response = await fetch("/api/dashboard/auth/refresh", {
    method: "POST",
    credentials: "same-origin",
  })
  if (!response.ok) return null

  const payload = (await response.json()) as SessionResponse
  if (!payload.authenticated) return null

  return {
    user: payload.user,
    expiresAt: payload.expiresAt,
    idleExpiresAt: payload.idleExpiresAt,
    absoluteExpiresAt: payload.absoluteExpiresAt,
  } as AuthSession
}

export async function performLogoutAction() {
  // Front-channel logout for the short-links app. Its session is a ten-minute
  // cache of this dashboard session, held as a cookie on the links origin that
  // this code cannot clear directly — but the two hosts are same-site, so a
  // credentialed no-cors ping to Kutt's /logout clears it in this browser
  // immediately. Best-effort and time-bounded: if the ping fails (or the
  // logout happens in another browser entirely), the ten-minute session cap
  // still ends links access on its own.
  // Only where the links app actually exists — on other stages the ping would
  // just wait out its timeout against a host that is not there.
  //
  // Fired and forgotten, with `keepalive` so it still lands after this
  // document is replaced. It used to be awaited behind a 3s timeout, which
  // made a best-effort ping — one that already has a ten-minute backstop —
  // able to hold someone on a dead dashboard for three seconds after they
  // clicked Log out. Nothing here reads its result, and nothing should wait
  // on it.
  if (shortLinksDeployed()) {
    void fetch(new URL("logout", shortLinksUrl()).toString(), {
      mode: "no-cors",
      credentials: "include",
      keepalive: true,
    }).catch(() => null)
  }

  // This one IS awaited, and has to be: it clears the session cookie, and
  // /login redirects straight back into the dashboard for anyone who still
  // has a live one. Same-origin and small, so it is the only thing between
  // the click and the new page.
  return fetch("/api/dashboard/auth/logout", {
    method: "POST",
    credentials: "same-origin",
  })
}

async function fetchServerSession(): Promise<AuthSession> {
  const response = await fetch("/api/dashboard/auth/session", {
    method: "GET",
    credentials: "same-origin",
  })
  if (!response.ok) {
    throw new Error("Could not read the dashboard session.")
  }

  const payload = (await response.json()) as SessionResponse
  if (!payload.authenticated || !payload.user || typeof payload.user !== "object") {
    throw new Error("Could not read the dashboard session.")
  }

  return {
    user: payload.user,
    expiresAt: payload.expiresAt,
    idleExpiresAt: payload.idleExpiresAt,
    absoluteExpiresAt: payload.absoluteExpiresAt,
  }
}
